/**
 * A manager putting their PIN in at the till.
 *
 * WHY THIS APPEARS INSIDE THE PAYMENT SHEET
 * -----------------------------------------
 * Because that is where the refusal happens. The cashier rings the order up,
 * applies the discount, presses Complete, and only then does the server say a
 * manager is needed. Sending them back to the discount screen to start again
 * would lose the payment they had already entered.
 *
 * So the box opens in place, the manager reaches over the cashier's shoulder,
 * and the same payment is sent again with their PIN attached. Nothing that was
 * typed is lost.
 *
 * WHY THE MANAGER IS TYPED IN RATHER THAN PICKED FROM A LIST
 * ---------------------------------------------------------
 * A dropdown would be quicker, and it would also be a list of exactly which
 * accounts can authorise money off a bill, readable by anyone who can open the
 * till. The whole approval check is built to avoid handing that out — it gives
 * the same answer to a wrong PIN and an unknown person for the same reason.
 * A dropdown here would undo that from the other side.
 *
 * It is slower. If the client would rather have short staff codes than typed
 * emails, that is a reasonable trade and a small change; it is not one to make
 * on their behalf.
 */
import { useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';

const field =
  'h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none';

export function ManagerApproval({ error, busy, onApprove }) {
  const [by, setBy] = useState('');
  const [pin, setPin] = useState('');

  const ready = by.trim().length > 0 && pin.length >= 4;

  /**
   * The first refusal is not a failure, it is the question being asked.
   *
   * Showing "that authorisation was not accepted" before anyone has typed
   * anything would be a lie, and the cashier would go looking for a fault
   * that is not there.
   */
  const asking = error?.code === 'APPROVAL_REQUIRED';

  return (
    <div className="mt-4 rounded-lg border border-warn/40 bg-warn-bg px-3.5 py-3">
      <p role="alert" className={`text-sm ${asking ? 'text-warn' : 'text-bad'}`}>
        {error?.message}
      </p>

      <div className="mt-3 flex flex-wrap gap-2">
        <label className="min-w-48 flex-1">
          <span className="mb-1 block text-xs text-ink-3">Manager</span>
          <input
            aria-label="Manager email"
            type="email"
            autoComplete="off"
            placeholder="manager@foodrun.pk"
            value={by}
            onChange={(e) => setBy(e.target.value)}
            className={field}
          />
        </label>

        <label className="w-32">
          <span className="mb-1 block text-xs text-ink-3">PIN</span>
          <input
            aria-label="Manager PIN"
            // Typed at a counter with people either side of it.
            type="password"
            inputMode="numeric"
            autoComplete="off"
            maxLength={8}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && ready && !busy) onApprove({ by: by.trim(), pin });
            }}
            className={`${field} tabular-nums`}
          />
        </label>
      </div>

      <div className="mt-3">
        <Button
          size="lg"
          className="w-full"
          busy={busy}
          disabled={!ready}
          onClick={() => onApprove({ by: by.trim(), pin })}
        >
          {busy ? 'Checking' : 'Approve and complete'}
        </Button>
      </div>
    </div>
  );
}
